import { FC, useEffect, useMemo } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import useDC from '@eneco/dc/useDC';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { basketMachine } from '@eneco/flows/machines/basketMachine';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRedirect } from '@eneco/hooks/redirect';
import { RadioGroup, RadioTile, Heading } from '@eneco/sparky';
import {
  DC_Products_Client_Old_Client_Models_BuildingCategory,
  DC_Products_Client_Old_Client_Models_BuildingSubCategory,
} from '@monorepo-types/dc';
import { usePlaceholderContent, useLayoutData } from '@sitecore/common';
import { DecarbonizationLeadFlowBusinessCategoriesStepRendering } from '@sitecore/types/DecarbonizationLeadFlowBusinessCategoriesStep';
import { useMachine } from '@xstate/react';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';

import {
  DecarbonisationLeadFlowContext,
  buildingCategoriesInOrder,
  categoriesWithOrderedSubCategories,
  BuildingCategoriesEnum,
  BuildingSubCategoriesEnum,
  CategoryWithSubCategory,
} from '../types';

const buildingCategoryField = 'buildingCategory';
const buildingSubcategoryField = 'buildingSubcategory';

interface FormFields {
  buildingCategory: string;
  buildingSubcategory?: string;
}

export const BusinessTypeStep: FC = () => {
  const { businessUnit, label } = useDC();
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<DecarbonisationLeadFlowContext>();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);
  const layoutData = useLayoutData();
  const redirect = useRedirect();
  const [, sendToBasketMachine] = useMachine(basketMachine);

  const data = usePlaceholderContent<{
    DecarbonizationLeadFlowBusinessCategoriesStep: DecarbonizationLeadFlowBusinessCategoriesStepRendering;
  }>();

  const { DecarbonizationLeadFlowBusinessCategoriesStep: { fields: textualData } = {} } = data;
  const businessTypeStepSchema = yup.object().shape({
    [buildingCategoryField]: yup.string().required(textualData?.choice?.buildingCategoriesList?.value?.requiredMessage),
    [buildingSubcategoryField]: yup
      .string()
      .required(textualData?.choice?.buildingSubcategoriesList?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
    resetField,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    resolver: typedYupResolver<FormFields>(businessTypeStepSchema),
    defaultValues: {
      buildingCategory: (flowContext.buildingCategory as BuildingCategoriesEnum) || undefined,
      buildingSubcategory: (flowContext.buildingSubcategory as BuildingSubCategoriesEnum) || undefined,
    },
  });

  const selectedBuildingCategory = watch(buildingCategoryField);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const { buildingCategory, buildingSubcategory } = getValues();
      if (
        buildingSubcategory === BuildingSubCategoriesEnum.OTHER &&
        buildingCategory === BuildingCategoriesEnum.Other
      ) {
        sendToBasketMachine({
          type: 'UPDATE_BASKET',
          config: {
            businessUnit,
            label,
          },
          id: flowContext.basketId,
          values: {
            ...flowContext,
            // @ts-ignore basket machine does not respect the extended flow context type
            buildingCategory,
            buildingSubcategory,
            path: '/gegevens/',
          },
        });
        redirect({
          link: `${layoutData.route.fields?.frontEndRootPath?.value}meer-informatie`,
        });
      } else {
        sendToFlowMachine({
          type: 'NEXT_PAGE',
          values: {
            ...flowContext,
            buildingCategory: buildingCategory as DC_Products_Client_Old_Client_Models_BuildingCategory,
            buildingCategoryLabel: buildingCategoriesTextualData[buildingCategory]?.label || '',
            buildingSubcategory: buildingSubcategory as DC_Products_Client_Old_Client_Models_BuildingSubCategory,
            buildingSubcategoryLabel: buildingSubcategory
              ? buildingSubcategoriesTextualData[buildingSubcategory]?.label || ''
              : '',
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const buildingCategoriesTextualData = useMemo(() => {
    return (
      textualData?.choice.buildingCategoriesList?.value?.options?.reduce<
        Record<string, (typeof textualData.choice.buildingCategoriesList.value.options)[0]>
      >((agr, item) => {
        agr[item.name] = item;
        return agr;
      }, {}) || {}
    );
  }, [textualData]);

  const buildingSubcategoriesTextualData = useMemo(() => {
    return (
      textualData?.choice.buildingSubcategoriesList?.value?.options?.reduce<
        Record<string, (typeof textualData.choice.buildingSubcategoriesList.value.options)[0]>
      >((agr, item) => {
        agr[item.name] = item;
        return agr;
      }, {}) || {}
    );
  }, [textualData]);

  const categoriesRadioGroup = useMemo(() => {
    return buildingCategoriesInOrder.map((item: DC_Products_Client_Old_Client_Models_BuildingCategory) => {
      if (buildingCategoriesTextualData[item]) {
        return (
          <RadioTile key={buildingCategoriesTextualData[item].name} value={buildingCategoriesTextualData[item].name}>
            {buildingCategoriesTextualData[item].label}
          </RadioTile>
        );
      }
      return null;
    });
  }, [buildingCategoriesTextualData]);

  const getSubcategoriesRadioGroup = () => {
    const listOfSubcategories =
      categoriesWithOrderedSubCategories[selectedBuildingCategory as BuildingCategoriesEnum] || [];

    if (selectedBuildingCategory && listOfSubcategories.length > 0) {
      return listOfSubcategories.map((item: CategoryWithSubCategory) => {
        const textualData = buildingSubcategoriesTextualData[item.subcategory];

        if (textualData) {
          return (
            <RadioTile key={textualData.name} value={textualData.name}>
              {textualData.label}
            </RadioTile>
          );
        }
        return null;
      });
    }
    return null;
  };

  useEffect(() => {
    const subcategories = categoriesWithOrderedSubCategories[selectedBuildingCategory as BuildingCategoriesEnum] || [];

    if (subcategories?.length === 0) {
      setValue('buildingSubcategory', BuildingSubCategoriesEnum.None);
    } else if (subcategories?.length === 1) {
      setValue('buildingSubcategory', subcategories[0].subcategory);
    } else {
      setValue('buildingSubcategory', undefined);
    }
  }, [buildingSubcategoriesTextualData, resetField, selectedBuildingCategory, setValue]);

  const subcategoriesTitle = textualData?.content?.subcategorySectionTitle?.value.replace(
    '%1$',
    buildingCategoriesTextualData[selectedBuildingCategory]?.label ?? '',
  );

  const subcategories = categoriesWithOrderedSubCategories[selectedBuildingCategory as BuildingCategoriesEnum] || [];
  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <RichText html={textualData?.content?.text?.value} />}
      </Header>
      <Controller
        control={control}
        name={buildingCategoryField}
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby={name}
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors?.[name]?.message}>
            {categoriesRadioGroup}
          </RadioGroup>
        )}
      />

      {selectedBuildingCategory && subcategories.length > 1 && (
        <>
          <Heading as={'h2'} size={'XS'}>{subcategoriesTitle}</Heading>
          <Controller
            control={control}
            name={buildingSubcategoryField}
            rules={{ required: textualData?.choice?.businessCategories?.value?.requiredMessage }}
            render={({ field: { onChange, value, name } }) => (
              <RadioGroup
                aria-labelledby={name}
                direction="column"
                name={name}
                value={value}
                onValueChange={onChange}
                error={errors?.[name]?.message}>
                {getSubcategoriesRadioGroup()}
              </RadioGroup>
            )}
          />
        </>
      )}

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
